import cs from 'classnames';
import React from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import { Template as ApiTemplate } from '../../../../../types/api';
import { TemplateImage } from '../TemplateImage/TemplateImage';
import { UiLoggerStore } from '../../../stores/UiLoggerStore';
import { TemplatesStore } from '../../../stores/TemplatesStore';
import { isElementInView } from '../../../../utils/isElementInView';
import { injectV2 } from '../../../injectV2';
import { TemplateFilters } from '../TemplateFilters/TemplateFilters';
import { useOnWindowScroll } from '../../../hooks/useOnWindowScroll';
import { useOnWindowResize } from '../../../hooks/useOnWindowResize';
import { ErrorReporterContext } from '../../../contexts/ErrorReporterContext';
import { mediaElementReadyStateToString } from '../../../../libs/mediaElementReadyStateToString';
import { useIsMounted } from '../../../hooks/useIsMounted';
import s from './Template.scss';
import { HoverPanel } from './HoverPanel/HoverPanel';
import { InfoPanel } from './InfoPanel/InfoPanel';
import { templateDataHooks } from './Template.dataHooks';
import { ModalBox } from './ModalBox/ModalBox';

interface TemplateProps {
  template: ApiTemplate;
  galleryDocIndex: number;
  dataHook?: string;
  uiLoggerStore: UiLoggerStore;
  templatesStore: TemplatesStore;
  forwardedRef?: (el: HTMLLIElement) => void;
  isLastItemHidden?: boolean;
  categorySlug: string | 'search' | null;
  subCategorySlug: string | null;
}

const TemplateCmp: React.FC<TemplateProps> = (props) => {
  const {
    dataHook,
    galleryDocIndex,
    template,
    forwardedRef,
    templatesStore,
    uiLoggerStore,
    isLastItemHidden,
    categorySlug,
    subCategorySlug,
  } = props;
  const isMounted = useIsMounted();
  const [hoverStartTime, setHoverStartTime] = React.useState<number | null>(null);
  const [isImageLoading, setIsImageLoading] = React.useState(false);
  const [isImageDelayLoading, setIsImageDelayLoading] = React.useState(false);
  const [videoStartedPlayTime, setVideoStartedPlayTime] = React.useState<number>(null);
  const [isModalVideoPreloading, setIsModalVideoPreloading] = React.useState(false);
  const [isModalBoxVisible, setIsModalBoxVisible] = React.useState(false);
  const [isShowVideoControls, setShowVideoControls] = React.useState(false);
  const errorReporter = React.useContext(ErrorReporterContext);
  const videoRef = React.useRef<HTMLVideoElement>();

  React.useEffect(() => {
    const onPlay = () => setVideoStartedPlayTime(Date.now());
    const currentVideoElement = videoRef.current;
    if (currentVideoElement) {
      currentVideoElement.addEventListener('play', onPlay);
      return () => currentVideoElement.removeEventListener('play', onPlay);
    }
  });

  const thumbnailRef = React.useRef<HTMLDivElement>();
  const checkboxRef = React.useRef<HTMLInputElement>();
  const infoTextRef = React.useRef<HTMLDivElement>();
  const showInfoButtonRef = React.useRef<HTMLLabelElement>();
  const previewButtonRef = React.useRef<HTMLAnchorElement>();

  const reportIfVisibleToUser = (): void => {
    if (templatesStore.wasTemplateInView(template.id)) {
      return;
    }

    if (thumbnailRef.current && isElementInView(thumbnailRef.current)) {
      templatesStore.addViewedTemplateId(template.id);
      uiLoggerStore.logAboveTheFoldImpression({
        template_id: template.siteId,
        galleryDocIndex,
      });
    }
  };
  React.useEffect(reportIfVisibleToUser, [templatesStore.itemsPerPageUUID]);
  const debouncedReportIfVisibleToUser = useDebounceCallback(reportIfVisibleToUser, 500);
  useOnWindowScroll(debouncedReportIfVisibleToUser);
  useOnWindowResize(debouncedReportIfVisibleToUser);

  const handleInfoCheckboxChange = () => {
    if (checkboxRef.current?.checked) {
      uiLoggerStore.logTemplateInfoShown();
    } else {
      uiLoggerStore.logTemplateInfoHidden();
    }
  };

  const togglePanel = (show: boolean, focus?: boolean) => {
    if (checkboxRef.current && checkboxRef.current.checked !== show) {
      const focusElem = show ? infoTextRef : showInfoButtonRef;

      checkboxRef.current.checked = show;
      handleInfoCheckboxChange();
      focus && focusElem && focusElem.current.focus();
    }
  };

  const onThumbnailEnter = () => {
    setHoverStartTime(Date.now());
  };

  const openModalBox = async () => {
    setIsModalBoxVisible(true);

    uiLoggerStore.logTemplateVideoPreviewOpened({
      siteId: template.siteId,
      category: categorySlug,
      subCategory: subCategorySlug,
    });

    try {
      await videoRef.current?.play();
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        setShowVideoControls(true);
      } else {
        errorReporter.reportError(error);
      }
    }
  };

  const closeModalBox = () => {
    setIsModalBoxVisible(false);
    previewButtonRef.current.focus();

    const videoBiData = videoRef.current
      ? {
          videoStartTime: videoStartedPlayTime,
          videoEndTime: Date.now(),
          videoPlaybackPosition: Math.round(videoRef.current.currentTime * 1000),
          videoFullDuration: Math.round(videoRef.current.duration * 1000),
          videoStatus: mediaElementReadyStateToString(videoRef.current),
        }
      : {};

    uiLoggerStore.logTemplateVideoPreviewClosed({
      siteId: template.siteId,
      category: categorySlug,
      subCategory: subCategorySlug,
      ...videoBiData,
    });
  };

  const onThumbnailLeave = () => {
    togglePanel(false);

    uiLoggerStore.logTemplateHover({
      galleryDocIndex,
      duration: Date.now() - (hoverStartTime || 0),
      product: template.title,
      templateId: template.siteId,
      siteId: template.siteId,
      hasVideo: !!template.videoSrc,
      category: categorySlug,
      subCategory: subCategorySlug,
    });
  };

  const showInfoCheckboxId = `template-info-toggle-${template.id}-${Math.random()}`;
  const templateTitleId = `template-title-${template.id}`;

  const imageLoadingAnimationDelayTimer = React.useRef<number>();

  React.useEffect(() => () => window.clearTimeout(imageLoadingAnimationDelayTimer.current), [template.id]);

  const imageLoadingStarted = () => {
    setIsImageLoading(true);
    setIsImageDelayLoading(true);
    window.clearTimeout(imageLoadingAnimationDelayTimer.current);
    imageLoadingAnimationDelayTimer.current = window.setTimeout(() => setIsImageDelayLoading(false), 300);
  };

  React.useEffect(() => {
    const startModalVideoPreloading = () => {
      if (!isModalVideoPreloading) {
        setIsModalVideoPreloading(true);
      }
    };
    const stopModalVideoPreloading = () => {
      setIsModalVideoPreloading(false);
    };

    const previewButton = previewButtonRef.current;
    previewButton.addEventListener('mouseover', startModalVideoPreloading);
    previewButton.addEventListener('mouseout', stopModalVideoPreloading);
    previewButton.addEventListener('focus', startModalVideoPreloading);

    return () => {
      previewButton.removeEventListener('mouseover', startModalVideoPreloading);
      previewButton.removeEventListener('mouseout', stopModalVideoPreloading);
      previewButton.removeEventListener('focus', startModalVideoPreloading);
    };
  }, [isModalVideoPreloading]);

  return (
    <li
      className={cs(s.template, { [s.lastItemHidden]: isLastItemHidden })}
      data-hook={dataHook}
      data-bi-gallery-doc-index={galleryDocIndex}
      data-bi-template-has-video={template.videoSrc ? 'true' : 'false'}
      ref={forwardedRef}
    >
      <div aria-labelledby={templateTitleId} role="region">
        <div className={s.hoverArea} onMouseEnter={onThumbnailEnter} onMouseLeave={onThumbnailLeave}>
          <div className={s.browserHead} />
          <div className={s.thumbnailWrapper} ref={thumbnailRef}>
            <div className={s.thumbnail}>
              <div
                className={cs(s.image, {
                  [s.animationOverImage]: isMounted,
                  [s.loading]: isImageLoading || isImageDelayLoading,
                })}
              >
                <TemplateImage
                  template={template}
                  dataHook={templateDataHooks.image()}
                  onLoadStart={() => imageLoadingStarted()}
                  onLoad={() => setIsImageLoading(false)}
                  categorySlug={categorySlug}
                  subCategorySlug={subCategorySlug}
                />
              </div>
              <input
                ref={checkboxRef}
                data-hook={templateDataHooks.checkbox()}
                type="checkbox"
                tabIndex={-1}
                className={s.checkbox}
                onChange={handleInfoCheckboxChange}
                id={showInfoCheckboxId}
              />
              <HoverPanel
                dataHook={templateDataHooks.hoverPanel()}
                checkboxId={showInfoCheckboxId}
                className={s.hoverPanel}
                template={template}
                galleryDocIndex={galleryDocIndex}
                onShowPanel={() => togglePanel(true)}
                openModal={openModalBox}
                showInfoButtonRef={showInfoButtonRef}
                previewButtonRef={previewButtonRef}
                categorySlug={categorySlug}
                subCategorySlug={subCategorySlug}
              />
              <InfoPanel
                dataHook={templateDataHooks.infoPanel()}
                className={s.infoPanel}
                checkboxId={showInfoCheckboxId}
                goodFor={template.info.goodFor}
                description={template.info.description}
                onHidePanel={() => togglePanel(false)}
                infoTextRef={infoTextRef}
              />
            </div>
          </div>
        </div>
        <div
          className={s.title}
          data-hook={templateDataHooks.title()}
          data-bi-origin="title"
          data-bi-element="template_title"
          data-bi-element-value={template.siteId}
          data-bi-element-category={categorySlug}
          data-bi-element-subcategory={subCategorySlug}
          aria-hidden="true"
          id={templateTitleId}
        >
          {template.title}
        </div>
        <TemplateFilters dataHook={templateDataHooks.matchedFilters()} template={template} />
      </div>
      {(isModalVideoPreloading || isModalBoxVisible) && (
        <ModalBox
          isVisible={isModalBoxVisible}
          isShowVideoControls={isShowVideoControls}
          videoRef={videoRef}
          dataHook={templateDataHooks.modalBox({ shown: isModalBoxVisible })}
          onClose={closeModalBox}
          template={template}
          galleryDocIndex={galleryDocIndex}
          categorySlug={categorySlug}
          subCategorySlug={subCategorySlug}
          ariaLabelledBy={templateTitleId}
        />
      )}
    </li>
  );
};

export const Template = injectV2('templatesStore', 'uiLoggerStore')(TemplateCmp);
